/**
 * Theme color palette definitions
 * Contains all color values used across light and dark themes
 */

export const colors = {
  // Shadow colors for dark theme
  darkShadowColor: 'rgba(71, 70, 74, 0.2)',
  darkShadowColor14: 'rgba(71, 70, 74, 0.14)',
  darkShadowColor12: 'rgba(71, 70, 74, 0.12)',

  // Shadow colors for light theme
  lightShadowColor: 'rgba(200, 197, 202, 0.2)',
  lightShadowColor14: 'rgba(200, 197, 202, 0.14)',
  lightShadowColor12: 'rgba(200, 197, 202, 0.12)',

  // Background colors
  darkBackground: '#1A1B1F',
  lightBackground: '#4487B0',
  menuSelectedDark: '#464646',
  menuSelectedLight: '#E1F2FD',

  // Border colors
  borderMainDark: '#464646',
  borderMainLight: '#E5E5E5',

  // Text colors (used in both themes)
  textSuccess: '#00B596',
  textDanger: '#EF6F6C',
  textWarning: '#ECA400',

  // Error color scale
  error: {
    // main: '#B12425',
    error_10: '#2B100F',
    error_20: '#58130F',
    error_30: '#851919',
    error_40: '#B12425',
    error_50: '#DE2927',
    error_60: '#E96D69',
    error_70: '#E96D69',
    error_80: '#F2A9A7',
    error_90: '#F7D4D3',
    error_95: '#FFEDEA',
    error_99: '#FFFBFF',
  },
  // Error color scale
  errorDark: {
    main: '#DE2927',
  },
  // Error color scale
  errorLight: {
    main: '#B12425',
  },

  // Success color scale
  success: {
    main: '#389355',
    success_10: '#122617',
    success_20: '#1C4A2A',
    success_30: '#2A6F40',
    success_40: '#389355',
    success_50: '#3FA75F',
    success_60: '#6EC388',
    success_70: '#B7DEC1',
    success_80: '#B7DDC1',
    success_90: '#B7DDC1',
    success_95: '#DAEEE0',
    success_99: '#EBFFF1',
  },

  // Primary colors for dark theme
  primaryDark: {
    main: '#FFF',
    dark: '#fff',
    contrastText: 'rgba(255, 251, 255, 1)',
    gray: '#414141',
    white: 'rgba(18, 18, 18, 1)',
    primary_0: '#1F00A4',
    primary_10: '#222222',
    primary_20: '#323232',
    primary_30: '#414141',
    primary_40: '#515151',
    primary_50: '#616161',
    primary_60: '#717171',
    primary_70: '#818181',
    primary_80: '#909090',
    primary_90: '#A0A0A0',
    primary_95: '#D0D0D0',
    primary_99: '#EFEFEF',
    primary_100: '#F5F5F5',
    primary_110: '#323232',
    primary_120: '#1A1B1F',
  },

  // Primary colors for light theme
  primaryLight: {
    main: '#1A1B1F',
    dark: 'rgba(18, 18, 18, 1)',
    contrastText: 'rgba(27, 27, 31, 1)',
    contrastTextBlack: '#333',
    gray: 'rgba(0, 0, 0, 0.12)',
    white: '#fff',
    primary_0: '#1F00A4',
    primary_10: '#222222',
    primary_20: '#323232',
    primary_30: '#414141',
    primary_40: '#515151',
    primary_50: '#616161',
    primary_60: '#717171',
    primary_70: '#818181',
    primary_80: '#909090',
    primary_90: '#A0A0A0',
    primary_95: '#D0D0D0',
    primary_99: '#EFEFEF',
    primary_100: '#F5F5F5',
    primary_110: '#323232',
  },

  // Info colors
  info: {
    main: '#A5A1FF',
    dark: 'rgb(2, 136, 209)',
  },

  // Secondary colors for dark theme
  secondaryDark: {
    main: '#70C4F7',
    contrastText: 'rgba(255, 251, 255, 1)',
    contrastTextBlack: '#333',
    paper: '#333333',
    white: '#fff',
    secondary_10: '#0364A1',
    secondary_20: '#0486D7',
    secondary_30: '#069AF3',
    secondary_40: '#1FA2F3',
    secondary_50: '#3AADF5',
    secondary_60: '#70C4F7',
    secondary_70: '#8CCFF9',
    secondary_80: '#A8DBFA',
    secondary_90: '#A8DBFA',
    secondary_95: '#C5E7FC',
    secondary_99: '#E5F5FF',
    neutral_10: '#1B1B1F',
    neutral_30: '#47464A',
    neutral_40: '#5F5E62',
    neutral_50: '#78767A',
    neutral_60: '#929094',
    neutral_70: '#ADAAAF',
    neutral_80: '#C8C5CA',
    neutral_90: '#E5E1E6',
    neutral_95: '#D0D0D0',
    neutral_99: '#FFFBFF',
    surface_5: '#1E1E1E',
    surface_7: '#232323',
    surface_8: '#252525',
    surface_9: '#272727',
    surface_11: '#2C2C2C',
    surface_14: '#333333',
    surface_15: '#353535',
    surface_16: '#383838',
    surface_22: '#3D3D3D',
    error_40: '#DE2927',
    success_40: '#8ED889',
    warning_70: '#E99B13',
  },

  // Secondary colors for light theme
  secondaryLight: {
    main: '#0486D7',
    contrastText: '#FFFFFF',
    contrastTextBlack: '#333333',
    paper: '#FFF',
    white: '#fff',
    dark: '#333',
    secondary_10: '#0364A1',
    secondary_20: '#0486D7',
    secondary_30: '#069AF3',
    secondary_40: '#1FA2F3',
    secondary_50: '#3AADF5',
    secondary_60: '#70C4F7',
    secondary_70: '#8CCFF9',
    secondary_80: '#A8DBFA',
    secondary_90: '#A8DBFA',
    secondary_95: '#C5E7FC',
    secondary_99: '#E5F5FF',
    primary_100: '#F5F5F5',
    neutral_10: '#1B1B1F',
    neutral_30: '#47464A',
    neutral_40: '#5F5E62',
    neutral_50: '#78767A',
    neutral_60: '#929094',
    neutral_70: '#ADAAAF',
    neutral_80: '#C8C5CA',
    neutral_90: '#E5E1E6',
    neutral_95: '#F3F0F4',
    neutral_99: '#FFFBFF',
    neutral_tint_40: '#F9F9F9',
    surface_5: '#1E1E1E',
    surface_7: '#232323',
    surface_8: '#252525',
    surface_9: '#272727',
    surface_11: '#2C2C2C',
    surface_14: '#333333',
    surface_15: '#353535',
    surface_16: '#383838',
    surface_22: '#3D3D3D',
    error_40: '#B12425',
    warning_70: '#E99B13',
    success_40: '#256C2A',
  },

  // Warning colors
  warning: {
    main: '#E99B13',
    mainDark: '#FFEEDD',
    Warning_95: '#FFEEDD',
    Warning_30: '#643F00',
  },
};

/**
 * Generate shadow array for Material-UI theme
 * @param {string} shadowColor - Primary shadow color
 * @param {string} shadowColor14 - Secondary shadow color with 0.14 opacity
 * @param {string} shadowColor12 - Tertiary shadow color with 0.12 opacity
 * @returns {Array<string>} Array of shadow definitions for Material-UI
 */
export const generateShadows = (shadowColor, shadowColor14, shadowColor12) => [
  'none',
  `0px 1px 3px ${shadowColor}, 0px 1px 1px ${shadowColor14}, 0px 2px 1px -1px ${shadowColor12}`,
  `0px 1px 5px ${shadowColor}, 0px 2px 2px ${shadowColor14}, 0px 3px 1px -2px ${shadowColor12}`,
  `0px 1px 8px ${shadowColor}, 0px 3px 4px ${shadowColor14}, 0px 3px 3px -2px ${shadowColor12}`,
  `0px 2px 4px -1px ${shadowColor}, 0px 4px 5px ${shadowColor14}, 0px 1px 10px ${shadowColor12}`,
  `0px 3px 5px -1px ${shadowColor}, 0px 5px 8px ${shadowColor14}, 0px 1px 14px ${shadowColor12}`,
  `0px 3px 5px -1px ${shadowColor}, 0px 6px 10px ${shadowColor14}, 0px 1px 18px ${shadowColor12}`,
  `0px 4px 5px -2px ${shadowColor}, 0px 7px 10px ${shadowColor14}, 0px 2px 16px ${shadowColor12}`,
  `0px 5px 5px -3px ${shadowColor}, 0px 8px 10px ${shadowColor14}, 0px 3px 14px ${shadowColor12}`,
  `0px 5px 6px -3px ${shadowColor}, 0px 9px 12px ${shadowColor14}, 0px 3px 16px ${shadowColor12}`,
  `0px 6px 6px -3px ${shadowColor}, 0px 10px 14px ${shadowColor14}, 0px 4px 18px ${shadowColor12}`,
  `0px 6px 7px -4px ${shadowColor}, 0px 11px 15px ${shadowColor14}, 0px 4px 20px ${shadowColor12}`,
  `0px 7px 8px -4px ${shadowColor}, 0px 12px 17px ${shadowColor14}, 0px 5px 22px ${shadowColor12}`,
  `0px 7px 8px -4px ${shadowColor}, 0px 13px 19px ${shadowColor14}, 0px 5px 24px ${shadowColor12}`,
  `0px 7px 9px -4px ${shadowColor}, 0px 14px 21px ${shadowColor14}, 0px 5px 26px ${shadowColor12}`,
  `0px 8px 9px -5px ${shadowColor}, 0px 15px 22px ${shadowColor14}, 0px 6px 28px ${shadowColor12}`,
  `0px 8px 10px -5px ${shadowColor}, 0px 16px 24px ${shadowColor14}, 0px 6px 30px ${shadowColor12}`,
  `0px 8px 11px -5px ${shadowColor}, 0px 17px 26px ${shadowColor14}, 0px 6px 32px ${shadowColor12}`,
  `0px 9px 11px -5px ${shadowColor}, 0px 18px 28px ${shadowColor14}, 0px 7px 34px ${shadowColor12}`,
  `0px 9px 12px -6px ${shadowColor}, 0px 19px 29px ${shadowColor14}, 0px 7px 36px ${shadowColor12}`,
  `0px 10px 13px -6px ${shadowColor}, 0px 20px 31px ${shadowColor14}, 0px 8px 38px ${shadowColor12}`,
  `0px 10px 13px -6px ${shadowColor}, 0px 21px 33px ${shadowColor14}, 0px 8px 40px ${shadowColor12}`,
  `0px 10px 14px -6px ${shadowColor}, 0px 22px 35px ${shadowColor14}, 0px 8px 42px ${shadowColor12}`,
  `0px 11px 14px -7px ${shadowColor}, 0px 23px 36px ${shadowColor14}, 0px 9px 44px ${shadowColor12}`,
  `0px 11px 15px -7px ${shadowColor}, 0px 24px 38px ${shadowColor14}, 0px 9px 46px ${shadowColor12}`,
];
